import { QueryHookOptions, gql } from '@apollo/client'
import {
  CarouselGroupAdBlockFragment,
  GroupAdBlock,
  LargeGroupAdBlockFragment,
  StandardGroupAdBlockFragment,
} from 'app/features/ads/blocks'
import { formatReactions } from 'app/features/group/helpers'
import { usePersistedCacheQuery } from 'app/hooks'
import { GroupAnnouncement, GroupPost, ReactionType } from 'app/types'
import { GroupEvent } from 'app/types/GroupEvent'
import { GroupVisibility } from 'app/types/GroupVisibility'

export const query = gql`
  query getUserFeed($page: Int!, $limit: Int!) {
    getUserFeed(page: $page, limit: $limit) {
      totalDocs
      page
      ads {
        id
        preferredPosition
        isBrandAd
        brand {
          id
          name
          logo {
            url
            alt
          }
          slug
        }
        title
        logo {
          url
          alt
        }
        ad {
          ${CarouselGroupAdBlockFragment.partialFragment}
          ${LargeGroupAdBlockFragment.partialFragment}
          ${StandardGroupAdBlockFragment.partialFragment}
        }
      }
      docs {
        id
        text
        link
        totalComments
        createdAt
        editedAt
        type
        group {
          id
          name
          totalMembers
          visibility
          profileImage {
            id
            sizes {
              small {
                url
              }
              medium {
                url
              }
            }
          }
          university {
            slug
          }
          universityCourse {
            slug
          }
          allowedUserTypes
          isOwner
          isModerator
        }
        creator {
          id
          fullName
          avatar {
            id
            sizes {
              small {
                url
              }
              medium {
                url
              }
            }
          }
          university {
            id
            name
          }
          subjectOfStudy {
            id
            name
          }
          yearOfStudy
        }
        images {
          id
          image {
            id
            width
            height
            url
          }
        }
        reactions {
          id
          reaction
          user {
            id
          }
        }

        # Announcement
        body
        scheduledAt

        # Event
        event {
          id
          name
          description
          startDate
          endDate
          createdAt
          location
          image {
            id
            url
            width
            height
          }
        }
      }
    }
  }
`

export interface UserFeedQueryInput {
  limit: number
  page: number
}

export interface UserFeedQueryOutput {
  getUserFeed: {
    page: number
    totalDocs: number
    ads: {
      id: string
      isBrandAd: boolean
      brand?: {
        id: string
        name: string
        logo: {
          url: string
          alt: string
        }
        slug: string
      }
      title?: string
      logo?: {
        url: string
        alt: string
      }
      link?: string
      ad: GroupAdBlock[]
    }[]
    docs: {
      type: 'post' | 'event' | 'announcement'
      id: string
      text: string
      link: string
      createdAt: string
      editedAt: string | null
      totalComments: number
      group: {
        id: string
        name: string
        visibility: GroupVisibility
        totalMembers: number
        profileImage: {
          sizes: {
            small: {
              url: string
            }
            medium: {
              url: string
            }
          }
        }
        university?: {
          slug: string
        }
        universityCourse?: {
          slug: string
        }
        allowedUserTypes: string[]
        isOwner: boolean
        isModerator: boolean
      }
      creator: {
        id: string
        fullName: string
        avatar: {
          sizes: {
            small: {
              url: string
            }
            medium: {
              url: string
            }
          }
        }
        university: {
          id: string
          name: string
        }
        subjectOfStudy: {
          id: string
          name: string
        }
        yearOfStudy: string
      }
      images: {
        image: {
          id: string
          width: number
          height: number
          url: string
        }
      }[]
      reactions: {
        id: string
        reaction: ReactionType
        user: {
          id: string
        }
      }[]
      body?: string
      scheduledAt?: string
      event?: {
        id: string
        name: string
        description: string
        location?: string
        startDate: string
        endDate?: string
        createdAt: string
        image?: {
          id: string
          url: string
          width: number
          height: number
        }
      }
    }[]
  }
}

const defaultVariables: UserFeedQueryInput = {
  page: 1,
  limit: 10,
}

export const useUserFeedQuery = (
  options: QueryHookOptions<UserFeedQueryOutput, UserFeedQueryInput>
) => usePersistedCacheQuery<UserFeedQueryOutput, UserFeedQueryInput>(query, options)

export const formatFn = (
  feedData?: UserFeedQueryOutput['getUserFeed']
): (GroupPost | GroupEvent | GroupAnnouncement)[] => {
  if (!feedData) return []
  const feed = feedData.docs || []
  return feed
    .map((feedItem) => {
      const group = {
        id: feedItem.group.id,
        name: feedItem.group.name,
        href: `/groups/${feedItem.group.id}`,
        profileImage: {
          small: feedItem.group.profileImage?.sizes.small.url || '',
        },
        university: feedItem.group.university,
        universityCourse: feedItem.group.universityCourse,
        allowedUserTypes: feedItem.group.allowedUserTypes || [],
        isOwner: feedItem.group.isOwner,
        isModerator: feedItem.group.isModerator,
      }

      const creator = {
        id: feedItem.creator.id,
        fullName: feedItem.creator.fullName,
        avatar: {
          medium: feedItem.creator.avatar?.sizes?.medium.url,
          small: feedItem.creator.avatar?.sizes?.small.url,
        },
        university: feedItem.creator.university,
        subjectOfStudy: feedItem.creator.subjectOfStudy,
        yearOfStudy: feedItem.creator.yearOfStudy,
      }

      if (feedItem.type === 'event' && feedItem.event) {
        const event = feedItem.event
        const formattedEvent: GroupEvent = {
          type: 'groupEvent',
          href: `/groups/events/${event.id}`,
          id: feedItem.id,
          eventId: event.id,
          name: event.name,
          description: event.description,
          location: event.location,
          startDate: event.startDate,
          endDate: event.endDate,
          createdAt: event.createdAt,
          creator: creator,
          group: group,
          ...(event.image && {
            image: {
              url: event.image.url,
              width: event.image.width,
              height: event.image.height,
            },
          }),
        }
        return formattedEvent
      }

      if (feedItem.type === 'post') {
        const reactions = formatReactions(feedItem.reactions)
        const formattedPost: GroupPost = {
          type: 'groupPost',
          href: `/groups/posts/${feedItem.id}`,
          id: feedItem.id,
          createdAt: feedItem.createdAt,
          editedAt: feedItem.editedAt,
          reactions,
          totalComments: feedItem.totalComments || 0,
          text: feedItem.text,
          creator: creator,
          group: group,
          ...(feedItem.images[0]?.image && {
            image: {
              url: feedItem.images[0].image.url,
              width: feedItem.images[0].image.width,
              height: feedItem.images[0].image.height,
            },
          }),
        }
        return formattedPost
      }

      if (feedItem.type === 'announcement') {
        const reactions = formatReactions(feedItem.reactions)
        const formattedAnnouncement: GroupAnnouncement = {
          type: 'groupAnnouncement',
          href: `/groups/posts/${feedItem.id}`,
          id: feedItem.id,
          createdAt: feedItem.createdAt,
          editedAt: feedItem.editedAt,
          totalComments: feedItem.totalComments || 0,
          title: feedItem.text,
          body: feedItem.body,
          scheduledDate: feedItem.scheduledAt,
          reactions,
          creator: creator,
          group: group,
        }
        return formattedAnnouncement
      }

      return null
    })
    .filter(Boolean) as (GroupPost | GroupEvent | GroupAnnouncement)[]
}

export const userFeedQuery = {
  query,
  defaultVariables,
  formatFn,
}
